import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  FileText, Search, Eye, Printer, Download, ShoppingCart,
  Calendar, Package, RefreshCw, Check, ArrowLeft, Plus, Minus, Trash2,
  Edit, X, ZoomIn, MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatJpWithAlias } from '@/utils/partDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyQuotationsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // View Quotation Dialog
  const [viewQuotation, setViewQuotation] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  
  // Review and Edit Order Dialog
  const [reviewQuotation, setReviewQuotation] = useState(null);
  const [editedItems, setEditedItems] = useState([]);
  const [orderRemarks, setOrderRemarks] = useState('');
  const [addProductSearch, setAddProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  
  // Image Zoom Dialog
  const [zoomImage, setZoomImage] = useState(null);
  
  // Check if user is from India (not Nepal/Bangladesh)
  const isIndianCustomer = user?.country === 'India' || (!user?.country && user?.state && !['Nepal', 'Bangladesh'].includes(user?.state));

  const fetchQuotations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/quotations/my/list?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuotations(response.data.quotations || []);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  // Filter quotations by search query
  const filteredQuotations = quotations.filter(q => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      q.quotation_id?.toLowerCase().includes(search) ||
      q.items?.some(item => 
        item.part_name?.toLowerCase().includes(search) ||
        item.jp_part_no?.toLowerCase().includes(search)
      )
    );
  });

  // Calculate basic price (price before 18% GST)
  const calculateBasicPrice = (taxPaidPrice) => {
    return (taxPaidPrice || 0) / 1.18;
  };

  // Open Review modal with editable items
  const openReviewModal = (quotation) => {
    setEditedItems(quotation.items.map(item => ({ ...item })));
    setOrderRemarks('');
    setReviewQuotation(quotation);
    setAddProductSearch('');
    setSearchResults([]);
  };

  // Update item quantity
  const updateItemQuantity = (index, newQty) => {
    if (newQty < 0) return;
    const updated = [...editedItems];
    updated[index].quantity = newQty;
    updated[index].subtotal = newQty * (updated[index].price || updated[index].mrp || 0);
    setEditedItems(updated);
  };

  // Remove item from edited list
  const removeItem = (index) => {
    const updated = editedItems.filter((_, i) => i !== index);
    setEditedItems(updated);
  };

  // Search products to add
  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchingProducts(true);
    try {
      const response = await axios.get(`${API}/search/universal?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setSearchingProducts(false);
    }
  };

  // Add product to edited items
  const addProductToOrder = (product) => {
    // Check if already in list
    const existingIndex = editedItems.findIndex(item => item.jp_part_no === product.jp_part_no);
    if (existingIndex >= 0) {
      // Increase quantity
      updateItemQuantity(existingIndex, editedItems[existingIndex].quantity + 1);
    } else {
      // Add new item
      const effectivePrice = product.discounted_price || product.mrp || 0;
      const newItem = {
        jp_part_no: product.jp_part_no,
        part_name: product.part_name,
        price: effectivePrice,
        discounted_price: effectivePrice,
        mrp: product.mrp || 0,
        quantity: 1,
        subtotal: effectivePrice,
        has_special_price: product.has_special_price || false,
        image: product.image
      };
      setEditedItems([...editedItems, newItem]);
    }
    setAddProductSearch('');
    setSearchResults([]);
    toast.success('Item added');
  };

  // Calculate edited total
  const editedTotal = editedItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);

  // Place order from review modal
  const placeOrderFromReview = async () => {
    if (editedItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    setPlacingOrder(true);
    try {
      const response = await axios.post(
        `${API}/quotations/my/${reviewQuotation.quotation_id}/place-order`,
        {
          items: editedItems,
          remarks: orderRemarks
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Order ${response.data.order_id} placed successfully!`);
      setReviewQuotation(null);
      fetchQuotations();
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setPlacingOrder(false);
    }
  };

  // Generate PDF for quotation - matching pending order format
  const generateQuotationPDF = (quotation) => {
    const doc = new jsPDF();
    const customerName = user?.company_name || user?.name || 'Customer';
    const currentDate = new Date().toLocaleDateString('en-IN', { 
      day: '2-digit', month: '2-digit', year: 'numeric' 
    }).replace(/\//g, '-');
    
    // Header - matching order format
    doc.setFontSize(18);
    doc.setTextColor(220, 38, 38); // Red color like orders
    doc.text('JAI PARAS SPARES', 105, 15, { align: 'center' });
    
    // Quotation badge
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text('QUOTATION', 105, 23, { align: 'center' });
    
    // Quotation info
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Quotation: ${quotation.quotation_id}`, 14, 35);
    doc.text(`Customer: ${user?.name || '-'}`, 14, 41);
    doc.text(`Company: ${customerName}`, 14, 47);
    doc.text(`Date: ${new Date(quotation.created_at).toLocaleDateString('en-IN')}`, 140, 35);
    // Discount percentage intentionally NOT shown on shared PDF (sales secret).
    
    const tableStartY = 55;
    
    // Calculate basic prices (pre-GST) for each item
    const tableData = quotation.items.map((item, index) => {
      const taxPaidUnitPrice = item.price || item.discounted_price || item.mrp || 0;
      const basicPricePerUnit = calculateBasicPrice(taxPaidUnitPrice);
      const basicAmount = basicPricePerUnit * item.quantity;
      
      return [
        (index + 1).toString(),
        item.jp_part_no,
        item.part_name?.length > 35 ? item.part_name.substring(0, 35) + '...' : (item.part_name || ''),
        item.quantity.toString(),
        basicPricePerUnit.toFixed(2),
        basicAmount.toFixed(2)
      ];
    });

    // Calculate totals
    const totalBasicAmount = quotation.items.reduce((sum, item) => {
      const taxPaidUnitPrice = item.price || item.discounted_price || item.mrp || 0;
      const basicPricePerUnit = calculateBasicPrice(taxPaidUnitPrice);
      return sum + (basicPricePerUnit * item.quantity);
    }, 0);
    
    const gstAmount = totalBasicAmount * 0.18;
    const packingCharge = quotation.packing_charge || 0;
    const courierCharge = quotation.courier_charge || 0;
    const totalCharges = packingCharge + courierCharge;
    const totalAmount = totalBasicAmount + gstAmount + totalCharges;
    
    // Generate PDF table
    autoTable(doc, {
      startY: tableStartY,
      head: [['S No', 'Jp Part No', 'Part Name', 'Quantity', 'Price', 'Amount']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [220, 38, 38], fontSize: 9, fontStyle: 'bold', halign: 'center' },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 28 },
        2: { cellWidth: 70 },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 28, halign: 'right' }
      },
      margin: { left: 14, right: 14 }
    });

    // Footer totals - matching order format
    const finalY = doc.lastAutoTable.finalY + 5;
    let currentY = finalY;
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text('Total Basic Amount:', 120, currentY);
    doc.text(totalBasicAmount.toFixed(2), 182, currentY, { align: 'right' });
    currentY += 6;
    
    doc.text('GST (18%):', 120, currentY);
    doc.text(gstAmount.toFixed(2), 182, currentY, { align: 'right' });
    currentY += 6;
    
    // Show packing charge if present
    if (packingCharge > 0) {
      doc.text('Packing Charge:', 120, currentY);
      doc.text(packingCharge.toFixed(2), 182, currentY, { align: 'right' });
      currentY += 6;
    }
    
    // Show courier charge if present
    if (courierCharge > 0) {
      doc.text('Courier Charge:', 120, currentY);
      doc.text(courierCharge.toFixed(2), 182, currentY, { align: 'right' });
      currentY += 6;
    }
    
    doc.setDrawColor(0);
    doc.line(120, currentY - 3, 182, currentY - 3);
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('TOTAL AMOUNT:', 120, currentY + 3);
    doc.text(totalAmount.toFixed(2), 182, currentY + 3, { align: 'right' });
    
    // Notes
    if (quotation.notes) {
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      doc.text(`Notes: ${quotation.notes}`, 14, currentY + 13);
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text('This is a computer generated quotation.', 105, 280, { align: 'center' });
    
    return { doc, fileName: `${customerName} - QUOTE - ${currentDate}.pdf` };
  };

  // Download PDF
  const handleDownload = (quotation) => {
    const { doc, fileName } = generateQuotationPDF(quotation);
    doc.save(fileName);
    toast.success('Quotation downloaded');
  };

  // Place order from quotation
  const handlePlaceOrder = async (quotation) => {
    if (!window.confirm(`Place order from quotation ${quotation.quotation_id}?`)) {
      return;
    }
    
    setPlacingOrder(true);
    try {
      const response = await axios.post(
        `${API}/quotations/my/${quotation.quotation_id}/place-order`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Order ${response.data.order_id} placed successfully!`);
      setViewQuotation(null);
      fetchQuotations();
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setPlacingOrder(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-blue-100 text-blue-700">Active</Badge>;
      case 'converted':
        return <Badge className="bg-green-100 text-green-700">Order Placed</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-blue-600" />
                <h1 className="text-xl font-bold text-gray-900">My Quotations</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center mb-6">
          <div className="flex gap-2">
            {['', 'active', 'converted'].map((status) => (
              <Button
                key={status || 'all'}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={statusFilter === status ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                {status === '' ? 'All' : status === 'converted' ? 'Ordered' : 'Active'}
              </Button>
            ))}
          </div>
          
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search quotations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="my-quotation-search"
            />
          </div>
          
          <Button variant="outline" size="sm" onClick={fetchQuotations} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Quotations List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading your quotations...</div>
        ) : filteredQuotations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No quotations found</p>
              <p className="text-sm text-gray-400 mt-2">
                Quotations from the admin will appear here
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredQuotations.map((quotation) => (
              <Card key={quotation.quotation_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-bold text-blue-600 text-lg">{quotation.quotation_id}</span>
                        {getStatusBadge(quotation.status)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(quotation.created_at).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                        <Package className="w-4 h-4" />
                        <span>{quotation.items?.length || 0} items</span>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">
                        ₹{(quotation.final_amount || 0).toLocaleString('en-IN')}
                      </div>
                      {quotation.converted_to_order_id && (
                        <div className="text-sm text-green-600 mt-1 flex items-center justify-end gap-1">
                          <Check className="w-4 h-4" />
                          Order: {quotation.converted_to_order_id}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setViewQuotation(quotation)}
                        data-testid={`view-my-quotation-${quotation.quotation_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(quotation)}
                      >
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                      {quotation.status === 'active' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => openReviewModal(quotation)}
                          data-testid={`review-order-${quotation.quotation_id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" /> Review & Place Order
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* View Quotation Dialog */}
      <Dialog open={!!viewQuotation} onOpenChange={() => setViewQuotation(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              Quotation {viewQuotation?.quotation_id}
              {viewQuotation && getStatusBadge(viewQuotation.status)}
            </DialogTitle>
          </DialogHeader>

          {viewQuotation && (
            <div className="space-y-4">
              {/* Quotation Info */}
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Date:</span>
                      <span className="ml-2 font-medium">
                        {new Date(viewQuotation.created_at).toLocaleDateString('en-IN')}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Items:</span>
                      <span className="ml-2 font-medium">{viewQuotation.items?.length || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Items with Thumbnails */}
              <Card>
                <CardHeader className="py-3 bg-gray-50">
                  <CardTitle className="text-sm">Items</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-80 overflow-y-auto">
                    <div className="space-y-2 p-2">
                      {viewQuotation.items?.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 border rounded-lg hover:bg-gray-50">
                          {/* Thumbnail */}
                          <div 
                            className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden border bg-gray-100 cursor-pointer hover:opacity-80"
                            onClick={() => item.image && setZoomImage(item.image)}
                          >
                            {item.image ? (
                              <img 
                                src={item.image} 
                                alt={item.part_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <Package className="w-6 h-6" />
                              </div>
                            )}
                            {item.image && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/20 transition-colors">
                                <ZoomIn className="w-4 h-4 text-white opacity-0 hover:opacity-100" />
                              </div>
                            )}
                          </div>
                          
                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <p className="font-mono text-sm text-blue-600">{formatJpWithAlias(item)}</p>
                            <p className="text-sm text-gray-700 truncate">{item.part_name}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                              <span>Qty: {item.quantity}</span>
                              <span>×</span>
                              <span>₹{(item.discounted_price || item.price || item.mrp || 0).toFixed(2)}</span>
                            </div>
                          </div>
                          
                          {/* Amount */}
                          <div className="text-right">
                            <p className="font-bold text-green-600">₹{(item.subtotal || 0).toFixed(2)}</p>
                            {isIndianCustomer && (
                              <p className="text-xs text-gray-400">(Incl. 18% GST)</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 border-t flex justify-between items-center">
                    <span className="font-bold">Total</span>
                    <div className="text-right">
                      <span className="text-xl font-bold text-green-600">
                        ₹{(viewQuotation.final_amount || 0).toLocaleString('en-IN')}
                      </span>
                      {isIndianCustomer && (
                        <p className="text-xs text-gray-400">(Inclusive of 18% GST)</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              {viewQuotation.notes && (
                <Card>
                  <CardContent className="p-4">
                    <span className="text-gray-500">Notes:</span>
                    <p className="mt-1">{viewQuotation.notes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Converted Order Info */}
              {viewQuotation.converted_to_order_id && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span>Order Placed: </span>
                    <span className="font-bold text-green-700">{viewQuotation.converted_to_order_id}</span>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => viewQuotation && handleDownload(viewQuotation)}
            >
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
            {viewQuotation?.status === 'active' && (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => {
                  setViewQuotation(null);
                  openReviewModal(viewQuotation);
                }}
                data-testid="review-order-dialog-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Review & Place Order
              </Button>
            )}
            <Button variant="outline" onClick={() => setViewQuotation(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review and Place Order Dialog */}
      <Dialog open={!!reviewQuotation} onOpenChange={() => setReviewQuotation(null)}>
        <DialogContent 
          className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white"
          preventClose={true}
          onCloseClick={() => setReviewQuotation(null)}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-green-600" />
              Review & Place Order
              <Badge className="bg-blue-100 text-blue-700 ml-2">
                From: {reviewQuotation?.quotation_id}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {reviewQuotation && (
            <div className="space-y-4">
              {/* Add Products */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add More Items
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search products to add..."
                      value={addProductSearch}
                      onChange={(e) => {
                        setAddProductSearch(e.target.value);
                        searchProducts(e.target.value);
                      }}
                      className="pl-9"
                    />
                    {searchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {searchResults.map((product) => (
                          <div
                            key={product.jp_part_no}
                            className="p-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b last:border-0"
                            onClick={() => addProductToOrder(product)}
                          >
                            {product.image ? (
                              <img src={product.image} alt="" className="w-10 h-10 object-cover rounded" />
                            ) : (
                              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                                <Package className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-mono text-sm text-blue-600">{formatJpWithAlias(product)}</p>
                              <p className="text-sm text-gray-600 truncate">{product.part_name}</p>
                            </div>
                            <div className="text-right">
                              {product.discounted_price && product.discounted_price < product.mrp ? (
                                <>
                                  <p className="text-xs text-gray-400 line-through">₹{(product.mrp || 0).toFixed(2)}</p>
                                  <p className={`font-medium ${product.has_special_price ? 'text-amber-600' : 'text-green-600'}`}>₹{(product.discounted_price || 0).toFixed(2)}</p>
                                </>
                              ) : (
                                <p className="font-medium text-green-600">₹{(product.mrp || 0).toFixed(2)}</p>
                              )}
                              <Plus className="w-4 h-4 text-green-500" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Editable Items List */}
              <Card>
                <CardHeader className="py-3 bg-gray-50">
                  <CardTitle className="text-sm">Order Items ({editedItems.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-64 overflow-y-auto">
                    {editedItems.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                        <p>No items in order</p>
                        <p className="text-sm">Search and add products above</p>
                      </div>
                    ) : (
                      <div className="space-y-2 p-2">
                        {editedItems.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 border rounded-lg">
                            {/* Thumbnail */}
                            <div 
                              className="w-12 h-12 flex-shrink-0 rounded overflow-hidden border bg-gray-100 cursor-pointer"
                              onClick={() => item.image && setZoomImage(item.image)}
                            >
                              {item.image ? (
                                <img src={item.image} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="w-5 h-5 text-gray-400" />
                                </div>
                              )}
                            </div>
                            
                            {/* Details */}
                            <div className="flex-1 min-w-0">
                              <p className="font-mono text-sm text-blue-600">{formatJpWithAlias(item)}</p>
                              <p className="text-sm text-gray-700 truncate">{item.part_name}</p>
                              <p className="text-xs text-gray-500">₹{(item.discounted_price || item.price || item.mrp || 0).toFixed(2)} each</p>
                            </div>
                            
                            {/* Quantity Controls */}
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0"
                                onClick={() => updateItemQuantity(idx, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateItemQuantity(idx, parseInt(e.target.value) || 1)}
                                className="w-16 h-8 text-center"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0"
                                onClick={() => updateItemQuantity(idx, item.quantity + 1)}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            {/* Amount & Remove */}
                            <div className="text-right">
                              <p className="font-bold text-green-600">₹{(item.subtotal || 0).toFixed(2)}</p>
                              {isIndianCustomer && (
                                <p className="text-xs text-gray-400">(Incl. GST)</p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                              onClick={() => removeItem(idx)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Total */}
                  <div className="p-4 bg-gray-50 border-t flex justify-between items-center">
                    <span className="font-bold">Order Total</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-green-600">
                        ₹{editedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                      {isIndianCustomer && (
                        <p className="text-xs text-gray-400">(Inclusive of 18% GST)</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Remarks */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Order Remarks (Optional)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Enter any special instructions or remarks for this order... (These will be printed on all slips)"
                    value={orderRemarks}
                    onChange={(e) => setOrderRemarks(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    These remarks will appear on picking slips and invoices
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setReviewQuotation(null)}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={placeOrderFromReview}
              disabled={placingOrder || editedItems.length === 0}
              data-testid="confirm-place-order-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              {placingOrder ? 'Placing Order...' : `Place Order (₹${editedTotal.toLocaleString('en-IN')})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Zoom Dialog */}
      <Dialog open={!!zoomImage} onOpenChange={() => setZoomImage(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/90">
          <button
            className="absolute top-2 right-2 z-10 bg-white/20 hover:bg-white/40 rounded-full p-2 text-white"
            onClick={() => setZoomImage(null)}
          >
            <X className="w-6 h-6" />
          </button>
          {zoomImage && (
            <img
              src={zoomImage}
              alt="Product"
              className="w-full h-auto max-h-[80vh] object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyQuotationsPage;
